import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Eye, FileText, Loader2, Pencil, Plus, Printer, Sparkles, Trash2 } from 'lucide-react';
import PEProfitReport from '../components/peProfit/PEProfitReport';
import PEProfitControlPanel from '../components/peProfit/PEProfitControlPanel';
import PEProfitPdfPreviewModal from '../components/peProfit/PEProfitPdfPreviewModal';
import { PE_PROFIT_EXPORT_CSS, PE_PROFIT_PAGE_CSS } from '../components/peProfit/peProfitPrintLayout';
import {
    generatePeProfitPdfBlob,
    printPeProfitPdfBlob,
} from '../components/peProfit/peProfitPdfExport';
import { generateAndPersistBankerPitch } from '../components/bankerPitch/generateAndPersistBankerPitch';
import { usePeProfitCalculations } from '../hooks/usePeProfitCalculations';
import useAppStore from '../store/index';
import type { PlanungsSzenario } from '../types/peProfit';

const PEProfitPageStyles = () => <style>{`${PE_PROFIT_PAGE_CSS}${PE_PROFIT_EXPORT_CSS}`}</style>;

const formatArchivDatum = (timestamp: number): string =>
    new Date(timestamp).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

interface PEProfitArchiveViewProps {
    archiv: PlanungsSzenario[];
    showCreateForm: boolean;
    newTitel: string;
    renamingId: string | null;
    renameTitel: string;
    onStartCreate: () => void;
    onCancelCreate: () => void;
    onNewTitelChange: (value: string) => void;
    onSubmitCreate: () => void;
    onOpenEntry: (id: string) => void;
    onDeleteEntry: (id: string) => void;
    onStartRename: (id: string, currentTitel: string) => void;
    onCancelRename: () => void;
    onRenameTitelChange: (value: string) => void;
    onSubmitRename: () => void;
}

const PEProfitArchiveView: React.FC<PEProfitArchiveViewProps> = ({
    archiv,
    showCreateForm,
    newTitel,
    renamingId,
    renameTitel,
    onStartCreate,
    onCancelCreate,
    onNewTitelChange,
    onSubmitCreate,
    onOpenEntry,
    onDeleteEntry,
    onStartRename,
    onCancelRename,
    onRenameTitelChange,
    onSubmitRename,
}) => {
    const openPremiumModal = useAppStore((state) => state.openPremiumModal);

    return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="carbon-weave-bg -mx-1 rounded-2xl border border-pe-border dark:border-[#2a2a2a] px-5 py-5 md:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]/80">
                    Premium Tool
                </p>
                <h1 className="text-3xl font-bold text-pe-text dark:text-white md:text-4xl">
                    <span className="italic text-pe-accent-text dark:text-[#E6C299]">PE</span>
                    <span className="font-bold text-pe-text dark:text-white">Profit</span> Archiv
                </h1>
                <p className="mt-2 text-sm text-pe-muted dark:text-slate-400">
                    Wählen Sie eine bestehende Rentabilitätsplanung oder erstellen Sie eine neue.
                </p>
            </div>
            <button
                type="button"
                onClick={() => openPremiumModal('pe-profit')}
                className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20"
            >
                Tarifübersicht
            </button>
        </div>
        </div>

        {!showCreateForm ? (
            <button
                type="button"
                onClick={onStartCreate}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#E6C299]/50 bg-[#E6C299]/5 px-4 py-4 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/10 md:w-auto md:px-6"
            >
                <Plus size={16} />
                Neue Rentabilitätsplanung erstellen
            </button>
        ) : (
            <div className="rounded-xl border border-pe-border dark:border-slate-700 bg-pe-card dark:bg-[#15171e] p-4 md:p-5">
                <h3 className="text-base font-bold text-pe-text dark:text-white">Neue Rentabilitätsplanung</h3>
                <p className="mt-1 text-sm text-pe-muted dark:text-slate-400">Geben Sie einen Titel für das neue Szenario an.</p>
                <label className="mt-4 block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                        Titel
                    </span>
                    <input
                        type="text"
                        value={newTitel}
                        onChange={(e) => onNewTitelChange(e.target.value)}
                        placeholder="z. B. Preiserhöhung Q3"
                        className="w-full rounded-lg border border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-[#0f1015] px-3 py-2 text-sm text-pe-text dark:text-white placeholder:text-stone-400 dark:placeholder:text-slate-500"
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') onSubmitCreate();
                        }}
                    />
                </label>
                <div className="mt-5 flex flex-wrap justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancelCreate}
                        className="rounded-lg border border-pe-border-strong dark:border-slate-600 px-4 py-2 text-sm font-semibold text-pe-muted dark:text-slate-300 hover:bg-pe-hover dark:hover:bg-slate-800"
                    >
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        onClick={onSubmitCreate}
                        className="rounded-lg bg-[#E6C299] px-4 py-2 text-sm font-bold text-slate-900 hover:bg-[#d4b088]"
                    >
                        Anlegen
                    </button>
                </div>
            </div>
        )}

        <div>
            <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-sm font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Szenario-Archiv
                </h2>
                {archiv.length > 0 && (
                    <span className="text-xs text-stone-500 dark:text-slate-500">
                        {archiv.length} {archiv.length === 1 ? 'Eintrag' : 'Einträge'}
                    </span>
                )}
            </div>

            {archiv.length === 0 ? (
                <div className="rounded-xl border border-dashed border-pe-border dark:border-slate-700 bg-pe-elevated/60 dark:bg-[#15171e]/50 px-4 py-10 text-center">
                    <p className="text-sm text-pe-muted dark:text-slate-400">
                        Noch keine Rentabilitätsplanungen gespeichert. Legen Sie die erste Planung an.
                    </p>
                </div>
            ) : (
                <ul className="space-y-2">
                    {archiv.map((entry) => (
                        <li
                            key={entry.id}
                            className="rounded-xl border border-pe-border dark:border-slate-700 bg-pe-card dark:bg-[#15171e] transition-colors hover:border-pe-border-strong dark:hover:border-slate-600"
                        >
                            {renamingId === entry.id ? (
                                <div className="space-y-3 p-4">
                                    <label className="block">
                                        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                                            Titel umbenennen
                                        </span>
                                        <input
                                            type="text"
                                            value={renameTitel}
                                            onChange={(e) => onRenameTitelChange(e.target.value)}
                                            className="w-full rounded-lg border border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-[#0f1015] px-3 py-2 text-sm text-pe-text dark:text-white"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') onSubmitRename();
                                                if (e.key === 'Escape') onCancelRename();
                                            }}
                                        />
                                    </label>
                                    <div className="flex flex-wrap justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={onCancelRename}
                                            className="rounded-lg border border-pe-border-strong dark:border-slate-600 px-3 py-2 text-xs font-semibold text-pe-muted dark:text-slate-300 hover:bg-pe-hover dark:hover:bg-slate-800"
                                        >
                                            Abbrechen
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onSubmitRename}
                                            className="rounded-lg bg-[#E6C299] px-3 py-2 text-xs font-bold text-slate-900 hover:bg-[#d4b088]"
                                        >
                                            Speichern
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                                    <button
                                        type="button"
                                        onClick={() => onOpenEntry(entry.id)}
                                        className="min-w-0 flex-1 text-left"
                                    >
                                        <p className="truncate font-semibold text-pe-text dark:text-white">{entry.titel}</p>
                                        <p className="mt-1 text-xs text-pe-muted dark:text-slate-400">
                                            {formatArchivDatum(entry.timestamp)} · Rentabilitätsplanung
                                        </p>
                                    </button>
                                    <div className="flex shrink-0 flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => onOpenEntry(entry.id)}
                                            className="inline-flex items-center gap-2 rounded-lg border border-pe-border-strong dark:border-slate-600 px-3 py-2 text-xs font-bold uppercase tracking-wider text-pe-text dark:text-white hover:bg-pe-hover dark:hover:bg-slate-800"
                                        >
                                            <FileText size={14} />
                                            Öffnen
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onStartRename(entry.id, entry.titel)}
                                            className="inline-flex items-center gap-2 rounded-lg border border-pe-border-strong dark:border-slate-600 px-3 py-2 text-xs font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-slate-800"
                                        >
                                            <Pencil size={14} />
                                            Umbenennen
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onDeleteEntry(entry.id)}
                                            className="inline-flex items-center gap-2 rounded-lg border border-red-900/50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-red-400 hover:bg-red-950/30"
                                        >
                                            <Trash2 size={14} />
                                            Löschen
                                        </button>
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    </div>
    );
};

const PEProfitPage: React.FC = () => {
    const [viewMode, setViewMode] = useState<'archive' | 'workspace'>('archive');
    const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newTitel, setNewTitel] = useState('');
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameTitel, setRenameTitel] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    const peProfit = usePeProfitCalculations();
    const planungsArchiv = useAppStore((state) => state.planungsArchiv ?? []);
    const activeSzenarioId = useAppStore((state) => state.activeSzenarioId);
    const createPlanungsSzenario = useAppStore((state) => state.createPlanungsSzenario);
    const loadPlanungsSzenario = useAppStore((state) => state.loadPlanungsSzenario);
    const renamePlanungsSzenario = useAppStore((state) => state.renamePlanungsSzenario);
    const deletePlanungsSzenario = useAppStore((state) => state.deletePlanungsSzenario);
    const hydratePeProfitFromClientData = useAppStore((state) => state.hydratePeProfitFromClientData);
    const setPeProfitWorkspace = useAppStore((state) => state.setPeProfitWorkspace);
    const clearActiveSzenarioBankerPitch = useAppStore((state) => state.clearActiveSzenarioBankerPitch);
    const openPremiumModal = useAppStore((state) => state.openPremiumModal);

    useEffect(() => {
        setPeProfitWorkspace('peProfit');
        hydratePeProfitFromClientData();
    }, [hydratePeProfitFromClientData, setPeProfitWorkspace]);

    const activeSzenario = useMemo(
        () => planungsArchiv.find((entry) => entry.id === activeSzenarioId) ?? null,
        [planungsArchiv, activeSzenarioId]
    );
    const hasBankerPitch = Boolean(activeSzenario?.bankerPitchText?.trim());
    const isJahresAnsicht = peProfit.ansichtsModus === 'jahr';

    const sortedArchiv = useMemo(
        () => [...planungsArchiv].sort((a, b) => b.timestamp - a.timestamp),
        [planungsArchiv]
    );

    const handlePrint = async () => {
        const source = document.getElementById('pe-profit-report-export');
        if (!source) return;
        try {
            const blob = await generatePeProfitPdfBlob(source);
            printPeProfitPdfBlob(blob);
        } catch (error) {
            console.error('PE-Profit-Druck fehlgeschlagen:', error);
            window.alert('Druckvorschau konnte nicht erstellt werden.');
        }
    };

    const handleClearBankerPitch = async () => {
        clearActiveSzenarioBankerPitch();
        try {
            const { persistClientDataImmediate } = await import('../config/firebaseSync');
            await persistClientDataImmediate();
        } catch (error) {
            console.error('Banker-Pitch-Löschen Sync fehlgeschlagen:', error);
        }
    };

    const handleGenerateBankerPitch = async () => {
        if (!activeSzenarioId) {
            window.alert(
                'Bitte zuerst ein Szenario aus dem Archiv öffnen oder eine neue Rentabilitätsplanung anlegen.'
            );
            return;
        }
        setIsGenerating(true);
        try {
            await generateAndPersistBankerPitch(peProfit);
        } catch (error) {
            console.error('generateBankerPitch fehlgeschlagen:', error);
            window.alert(
                error instanceof Error
                    ? error.message
                    : 'KI-Text konnte nicht generiert werden. Bitte später erneut versuchen.'
            );
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSubmitCreate = () => {
        const titel = newTitel.trim() || 'Rentabilitätsplanung';
        createPlanungsSzenario(titel, 'start');
        setShowCreateForm(false);
        setNewTitel('');
        setViewMode('workspace');
    };

    const handleOpenEntry = (id: string) => {
        loadPlanungsSzenario(id);
        setViewMode('workspace');
    };

    const handleDeleteEntry = (id: string) => {
        const entry = planungsArchiv.find((item) => item.id === id);
        const label = entry?.titel?.trim() || 'diese Rentabilitätsplanung';
        if (!window.confirm(`„${label}“ wirklich unwiderruflich löschen?`)) return;
        if (renamingId === id) {
            setRenamingId(null);
            setRenameTitel('');
        }
        deletePlanungsSzenario(id);
    };

    const handleSubmitRename = () => {
        if (!renamingId) return;
        renamePlanungsSzenario(renamingId, renameTitel.trim() || 'Rentabilitätsplanung');
        setRenamingId(null);
        setRenameTitel('');
    };

    return (
        <>
            <PEProfitPageStyles />
            <PEProfitPdfPreviewModal
                isOpen={isPdfPreviewOpen}
                onClose={() => setIsPdfPreviewOpen(false)}
            />
            <div className="min-h-screen bg-pe-bg dark:bg-[#030303] p-4 md:p-6">
                <div className="w-full">
                    {viewMode === 'archive' && (
                        <PEProfitArchiveView
                            archiv={sortedArchiv}
                            showCreateForm={showCreateForm}
                            newTitel={newTitel}
                            renamingId={renamingId}
                            renameTitel={renameTitel}
                            onStartCreate={() => {
                                setShowCreateForm(true);
                                setRenamingId(null);
                            }}
                            onCancelCreate={() => {
                                setShowCreateForm(false);
                                setNewTitel('');
                            }}
                            onNewTitelChange={setNewTitel}
                            onSubmitCreate={handleSubmitCreate}
                            onOpenEntry={handleOpenEntry}
                            onDeleteEntry={handleDeleteEntry}
                            onStartRename={(id, currentTitel) => {
                                setShowCreateForm(false);
                                setRenamingId(id);
                                setRenameTitel(currentTitel);
                            }}
                            onCancelRename={() => {
                                setRenamingId(null);
                                setRenameTitel('');
                            }}
                            onRenameTitelChange={setRenameTitel}
                            onSubmitRename={handleSubmitRename}
                        />
                    )}

                    {viewMode === 'workspace' && (
                        <>
                            <div className="pe-profit-no-print mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div className="carbon-weave-bg rounded-2xl border border-pe-border dark:border-[#2a2a2a] px-5 py-4 md:px-6">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('archive')}
                                        className="mb-3 inline-flex items-center gap-2 rounded-lg border border-pe-border-strong dark:border-slate-600 px-3 py-2 text-xs font-bold uppercase tracking-wider text-pe-text dark:text-slate-200 hover:bg-pe-hover dark:hover:bg-slate-800"
                                    >
                                        <ArrowLeft size={14} />
                                        Zurück zum Archiv
                                    </button>
                                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]/80">
                                        Premium Tool
                                    </p>
                                    <h1 className="text-3xl font-bold text-pe-text dark:text-white md:text-4xl">
                                        <span className="italic text-pe-accent-text dark:text-[#E6C299]">PE</span>
                                        <span className="font-bold text-pe-text dark:text-white">Profit</span> Cockpit
                                    </h1>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => openPremiumModal('pe-profit')}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/10 px-5 py-3 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20"
                                    >
                                        Tarifübersicht
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsPdfPreviewOpen(true)}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#E6C299] to-[#b48a56] px-5 py-3 text-sm font-black uppercase tracking-widest text-black shadow-[0_0_20px_rgba(230,194,153,0.25)] hover:from-[#f0d3b3] hover:to-[#cfa674]"
                                    >
                                        <Eye size={17} />
                                        PDF-Vorschau
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handlePrint()}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-pe-border-strong dark:border-slate-600 bg-pe-elevated dark:bg-slate-800 px-5 py-3 text-sm font-bold uppercase tracking-wider text-pe-text dark:text-slate-200 hover:bg-pe-hover dark:hover:bg-slate-700"
                                    >
                                        <Printer size={17} />
                                        Drucken
                                    </button>
                                    <div className="flex flex-col items-stretch gap-1 sm:items-end">
                                        <div className="flex flex-wrap justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => void handleGenerateBankerPitch()}
                                                disabled={hasBankerPitch || isGenerating}
                                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#E6C299]/50 bg-[#E6C299]/10 px-5 py-3 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20 disabled:cursor-default disabled:opacity-70"
                                            >
                                                {isGenerating ? (
                                                    <Loader2 size={17} className="animate-spin" />
                                                ) : (
                                                    <Sparkles size={17} />
                                                )}
                                                {isGenerating ? 'KI generiert...' : 'KI-Text für die Bank generieren'}
                                            </button>
                                            {hasBankerPitch ? (
                                                <button
                                                    type="button"
                                                    onClick={() => void handleClearBankerPitch()}
                                                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-5 py-3 text-sm font-bold uppercase tracking-wider text-red-300 hover:bg-red-500/20"
                                                    title="KI-Text löschen"
                                                >
                                                    <Trash2 size={17} />
                                                    Löschen
                                                </button>
                                            ) : null}
                                        </div>
                                        {!isJahresAnsicht && !hasBankerPitch ? (
                                            <p className="text-[11px] text-pe-muted dark:text-slate-400">
                                                Schaltet für die Generierung automatisch auf Jahresansicht um
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div id="pe-profit-print-root" className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
                                <div className="min-w-0 pe-profit-no-print">
                                    <PEProfitControlPanel />
                                </div>
                                <div id="pe-profit-report-export" className="min-w-0">
                                    <PEProfitReport hideInlineBankerPitchButton />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default PEProfitPage;
